import { dayLabel } from "@/lib/calendar-grid";
import { JobChip, type CalendarJob } from "./job-chip";

// Free of @/hooks/use-api (no supabase import) so it renders in a harness and
// unit tests without env vars — the container owns the data fetch.

/**
 * Single-day view — the rep's "what does my day look like" (ADV-786). A full
 * absolute day header (§4.3) over the day's jobs as a stacked list of chips.
 * Renders a Type-2 teach line when the day is clear. Hour-axis timeline is out
 * of scope — `scheduled_date` is day-granular (no start/end times in the data),
 * so a vertical job list is the honest representation, not a faked hour grid.
 */
export function CalendarDayView({
  date,
  jobs,
  isToday,
  onOpenJob,
}: {
  date: Date;
  jobs: CalendarJob[];
  isToday: boolean;
  onOpenJob: (id: string) => void;
}) {
  return (
    <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
      <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] px-4 py-3">
        <h3 className="typo-label text-[color:var(--color-text-hi)]">
          {dayLabel(date)}
        </h3>
        {isToday && (
          <span className="rounded-[var(--radius-sm)] px-1.5 py-0.5 text-xs ring-1 ring-[color:var(--color-accent)] text-[color:var(--color-accent)]">
            Today
          </span>
        )}
        <span className="ml-auto typo-mono text-xs tabular-nums text-[color:var(--color-text-lo)]">
          {jobs.length} {jobs.length === 1 ? "job" : "jobs"}
        </span>
      </div>

      {jobs.length === 0 ? (
        <p className="px-4 py-10 text-center typo-label normal-case tracking-normal text-[color:var(--color-text-lo)]">
          No jobs scheduled for this day.
        </p>
      ) : (
        <div className="space-y-1 p-3">
          {jobs.map((job) => (
            <JobChip key={job.id} job={job} onOpen={() => onOpenJob(job.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
